import type { PSAAdapter } from "@/lib/adapters/base";
import { ConnectWiseAdapter } from "@/lib/adapters/connectwise";

/** Minimum confidence for an adapter to be considered an auto-detect match. */
export const MIN_CONFIDENCE = 0.5;

/** An adapter paired with its detection confidence for a set of headers. */
export interface DetectionResult {
  adapter: PSAAdapter;
  name: string;
  confidence: number;
}

/** Registry of PSA adapters with confidence-based auto-detection. */
export class AdapterRegistry {
  private readonly adapters: PSAAdapter[] = [];

  /** Register an adapter. Registration order breaks confidence ties. */
  register(adapter: PSAAdapter): void {
    this.adapters.push(adapter);
  }

  /** All registered adapters, in registration order. */
  get all(): readonly PSAAdapter[] {
    return this.adapters;
  }

  /**
   * Score every adapter against `headers`, sorted by confidence descending.
   * `Array.prototype.sort` is stable, so ties keep registration order.
   */
  detectAll(headers: string[]): DetectionResult[] {
    return this.adapters
      .map((adapter) => ({ adapter, name: adapter.name, confidence: adapter.detect(headers) }))
      .sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * The best-matching adapter at or above {@link MIN_CONFIDENCE}, or `null`.
   * Ties are broken by registration order (first registered wins).
   */
  detect(headers: string[]): DetectionResult | null {
    let best: DetectionResult | null = null;
    for (const adapter of this.adapters) {
      const confidence = adapter.detect(headers);
      if (best === null || confidence > best.confidence) {
        best = { adapter, name: adapter.name, confidence };
      }
    }
    if (best === null || best.confidence < MIN_CONFIDENCE) return null;
    return best;
  }
}

/** Build a registry pre-populated with the built-in auto-detect adapters. */
export function buildDefaultRegistry(): AdapterRegistry {
  const registry = new AdapterRegistry();
  registry.register(new ConnectWiseAdapter());
  return registry;
}
